// ─────────────────────────────────────────────────────────────────────────────
//  Dust & Wipes Operations Hub — Zod Schemas
//  Phase 1 of the TypeScript migration.
//
//  STRATEGY: "Coerce + warn" mode (per /anthropic-skills:10x-vision plan).
//  - z.coerce.* auto-converts strings → numbers, etc. (kills the entire bug
//    class that produced ₦228 quadrillion portfolio totals + .split crashes)
//  - .catch(default) means bad data never breaks the UI; we warn + fall back
//  - .passthrough() preserves unknown fields so future-added columns don't
//    silently get stripped on round-trip writes
//
//  These schemas reflect what is *actually* in Supabase as of 2026-05, not an
//  ideal model — Phase 4 of the migration will tighten them. For now: make
//  the runtime safer without breaking a single existing record.
// ─────────────────────────────────────────────────────────────────────────────

import { z } from "zod";

// ── Reusable field primitives ────────────────────────────────────────────────
// num: coerces "100" → 100, null/undefined/garbage → 0
const num = z.coerce.number().catch(0);
// numOpt → field always present after parse (0 default). Kept the name for
// migration ergonomics; the `Opt` historically meant "optional in DB", not
// "may be undefined at runtime". Phase 5b made the runtime type non-optional
// so strict-null TypeScript stays tractable across 16 pages of code.
const numOpt = z.coerce.number().catch(0);

// str: coerces 123 → "123", null/undefined → ""
const str = z.coerce.string().catch("");
// strOpt: same convention as numOpt — always returns a string (empty default).
const strOpt = z.coerce.string().catch("");

// ID can come in as string (most rows) or number (legacy site reports)
const idField = z.union([z.string(), z.number()]).transform(String).catch("");

// boolean: handles "true"/"false" strings, 1/0, etc.
const bool = z.coerce.boolean().catch(false);

// String array — defaults to [] if anything weird shows up
const strArr = z.array(z.string()).catch([]).default([]);

// ── 1. Clients ───────────────────────────────────────────────────────────────
export const ClientSchema = z.object({
  id: idField,
  name: str,
  cat: strOpt,            // category (Corporate, Healthcare, etc.)
  svc: strOpt,            // service type (Cleaning / Pest Control / Both)
  addr: strOpt,           // address
  cp: strOpt,             // contact person
  phone: strOpt,
  email: strOpt,
  cs: strOpt,             // contract start date (YYYY-MM-DD)
  ce: strOpt,             // contract end date (YYYY-MM-DD)
  sal: numOpt,            // salary component (₦)
  con: numOpt,            // consumables (₦)
  sc: numOpt,             // service charge (₦)
  vat: numOpt,            // VAT (₦)
  tot: numOpt,            // total annual value (₦)
  cleaners: strArr,
  duty: strOpt,           // work days (Mon-Fri, Mon-Sat)
  serviceFreq: strOpt,    // Daily/Weekly/Bi-weekly/Monthly/Quarterly/One-Time
  status: strOpt,         // derived field — sometimes persisted
}).passthrough();

// ── 2. Jobs ──────────────────────────────────────────────────────────────────
export const JobSchema = z.object({
  id: idField,
  clientName: strOpt,
  clientPhone: strOpt,
  svc: strOpt,
  date: strOpt,           // scheduled date
  createdAt: strOpt,      // ISO timestamp (Phase-1 addition, may be missing on legacy)
  sup: strOpt,            // supervisor
  techs: strOpt,          // crew (comma-separated)
  status: strOpt,
  notes: strOpt,
  loc: strOpt,
  checkIn: strOpt.nullable(),
  checkOut: strOpt.nullable(),
  sourceRequestId: strOpt,
  signOff: z.any().optional(),
  autoScheduled: bool.optional(),
}).passthrough();

// ── 3. Users (app users — Admin / Supervisor / Technician) ───────────────────
export const UserSchema = z.object({
  id: idField,
  name: str,
  email: strOpt,
  username: strOpt,       // phone number for technicians
  role: strOpt,
  initial: strOpt,
  dob: strOpt,
  pwHash: strOpt,         // SHA-256 hashed password
  password: strOpt,       // legacy — should be moved to pwHash on save
  // Phase 6: ISO timestamp of the last Dashboard view. Stamped by
  // non-Admin Dashboards on mount so Admin can spot "X hasn't seen
  // their plate this week" without asking.
  lastSeenDashboard: strOpt,
}).passthrough();

// ── 4. Staff (field employees, separate from app users) ──────────────────────
export const StaffSchema = z.object({
  id: idField,
  name: str,
  role: strOpt,
  category: strOpt,       // Office Staff / Cleaning Staff / Gardening Staff
  site: strOpt,
  phone: strOpt,
  email: strOpt,
  dob: strOpt,
  startDate: strOpt,
  employmentType: strOpt,
  workDays: strOpt,
  homeAddress: strOpt,
  emergencyContact: strOpt,
  emergencyPhone: strOpt,
  emergencyAddress: strOpt,
  accountName: strOpt,
  accountNumber: strOpt,
  bankName: strOpt,
}).passthrough();

// ── 5. Service Requests ──────────────────────────────────────────────────────
export const RequestSchema = z.object({
  id: idField,
  clientName: strOpt,
  clientPhone: strOpt,
  svc: strOpt,
  loc: strOpt,
  prefDate: strOpt,
  src: strOpt,            // Phone / WhatsApp / Email / Walk-in / Website / Referral
  status: strOpt,         // Pending / Converted / Declined
  notes: strOpt,
  created: strOpt,        // YYYY-MM-DD when request was logged
  // Phase 6 (workflow tooling): when a Service Request is assigned to a
  // supervisor inline on the Dashboard, these fields are stamped. Empty
  // string means unassigned.
  assignedTo: strOpt,     // Supervisor name (or staff id for stricter wiring)
  assignedAt: strOpt,     // ISO timestamp of assignment
  assignedBy: strOpt,     // Admin who performed the assignment
}).passthrough();

// ── 6. Site Reports ──────────────────────────────────────────────────────────
// Crew can be either an array (StaffMultiPicker) or string (textarea) — both
// allowed; downstream code defends with Array.isArray + String() coercion.
export const SiteReportSchema = z.object({
  id: idField,
  supervisorName: strOpt,
  supervisorEmail: strOpt,
  clientName: strOpt,
  address: strOpt,
  arrivalDate: strOpt,
  arrivalTime: strOpt,
  departureDate: strOpt,
  departureTime: strOpt,
  submittedAt: strOpt,    // ISO timestamp
  gpsLat: strOpt,
  gpsLng: strOpt,
  gpsAcquired: bool.optional(),
  jobType: strOpt,
  contractType: strOpt,
  serviceCategory: strArr,
  cleaningTasks: strArr,
  pestTasks: strArr,
  otherTasks: strOpt,
  crewMembers: z.union([z.string(), z.array(z.string())]).catch("").optional(),
  equipment: strArr,
  supplies: strArr,
  pesticidesUsed: strOpt,
  activeIngredients: strOpt,
  cleanlinessRating: numOpt,
  adherenceRating: numOpt,
  qualityNotes: strOpt,
  ppeWorn: strOpt,
  safeHandling: strOpt,
  incidents: strOpt,
  incidentDetails: strOpt,
  clientPresent: strOpt,
  clientContactName: strOpt,
  clientFeedback: strOpt,
  satisfactionLevel: strOpt,
  additionalRequirements: strOpt,
  additionalReqDetails: strOpt,
  photos: z.array(z.any()).catch([]).optional(),
  operationalNotes: strOpt,
  overallAssessment: strOpt,
  signatureName: strOpt,
  signatureTimestamp: strOpt,
}).passthrough();

// ── 7. Imprest Fund Records ──────────────────────────────────────────────────
export const ImprestSchema = z.object({
  id: idField,
  title: strOpt,
  holder: strOpt,
  fundType: strOpt,
  branch: strOpt,
  month: strOpt,          // YYYY-MM format
  amount: num,
  originalAmount: numOpt,
  releaseDate: strOpt,
  deadline: strOpt,
  purpose: strOpt,
  status: strOpt,         // Active / Pending Reconciliation / Closed / Flagged
  expenses: z.array(z.any()).catch([]).optional(),
  topups: z.array(z.any()).catch([]).optional(),
  isCarryForward: bool.optional(),
  carryForwardAmount: numOpt,
  carriedFrom: strOpt,
  closedPeriod: strOpt,
}).passthrough();

// ── 8. Inventory ─────────────────────────────────────────────────────────────
export const InventorySchema = z.object({
  id: idField,
  item: str,
  cat: strOpt,
  unit: strOpt,
  qty: num,
  reorder: numOpt,
  cost: numOpt,
}).passthrough();

// ── 9. Requisitions ──────────────────────────────────────────────────────────
export const RequisitionSchema = z.object({
  id: idField,
  site: strOpt,
  // month here is an INTEGER (0-11), not YYYY-MM like imprests
  month: z.coerce.number().int().min(0).max(11).catch(0),
  year: z.coerce.number().int().catch(() => new Date().getFullYear()),
  budgetCap: numOpt,
  submittedBy: strOpt,
  status: strOpt,         // Pending / Approved / Rejected / Forwarded
  items: z.array(z.any()).catch([]).optional(),
  reviewedBy: strOpt,
  reviewedAt: strOpt,
}).passthrough();

// ── 10. Supply Master Catalogue ──────────────────────────────────────────────
export const SupplyItemSchema = z.object({
  id: idField,
  name: str,
  cat: strOpt,
  unit: strOpt,
  cost: num,
  active: bool.optional(),
}).passthrough();

// ── 11. Pest Schedules ───────────────────────────────────────────────────────
export const ScheduleSchema = z.object({
  id: idField,
  clientName: strOpt,
  service: strOpt,
  dateCarriedOut: strOpt,
  dueDate: strOpt,
  notes: strOpt,
  overdue: bool.optional(),
}).passthrough();

// ── 12. Absences ─────────────────────────────────────────────────────────────
export const AbsenceSchema = z.object({
  id: idField,
  cleaner: strOpt,
  site: strOpt,
  startDate: strOpt,
  endDate: strOpt,
  reason: strOpt,
  leaveType: strOpt,      // Sick / Annual / Emergency / AWOL / Maternity / Other
  needsReplacement: bool.optional(),
  deductionAmount: numOpt,
  status: strOpt,         // Absent Logged / Cover Assigned / Completed / Sent to Finance
}).passthrough();

// ── 13. Covers ───────────────────────────────────────────────────────────────
export const CoverSchema = z.object({
  id: idField,
  absentCleaner: strOpt,
  replacement: strOpt,
  site: strOpt,
  startDate: strOpt,
  endDate: strOpt,
  days: numOpt,
  compensation: bool.optional(),
  coverAmount: numOpt,
  remarks: strOpt,
}).passthrough();

// ── 14. Site Assessments ─────────────────────────────────────────────────────
// Empty in DB right now — permissive schema until real records are seen.
export const AssessmentSchema = z.object({
  id: idField,
}).passthrough();

// ── 15. Contacts (uses top-level columns, not record JSONB) ──────────────────
export const ContactSchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  name: str,
  phone: strOpt,
  email: strOpt,
  address: strOpt,
  notes: strOpt,
  source: strOpt,
}).passthrough();

// ── 16. Tasks (Phase 6 — workflow tooling) ──────────────────────────────────
// Manually-assigned work items that surface on the assignee's Dashboard.
// Distinct from Jobs (which are client-facing service appointments) and
// ServiceRequests (which are inbound). Tasks are internal work: "Inspect
// Site X", "Audit petty cash", "Follow up with Beta Office contact".
export const TaskSchema = z.object({
  id: idField,
  title: str,
  description: strOpt,
  assignee: strOpt,         // staff/user name. Empty = unassigned (Admin pool).
  assigneeRole: strOpt,     // "Supervisor" | "Technician" | "Finance" | "Admin"
  priority: strOpt,         // "Low" | "Normal" | "High" | "Urgent"
  status: strOpt,           // "Pending" | "In Progress" | "Done" | "Cancelled"
  dueDate: strOpt,          // YYYY-MM-DD
  weekOf: strOpt,           // YYYY-MM-DD of the Monday of the assigned week
  createdBy: strOpt,
  createdAt: strOpt,
  completedAt: strOpt,
  // Provenance — if this task was materialized from a recurring template, the
  // template id lives here so we can avoid double-creating on re-runs.
  templateId: strOpt,
}).passthrough();

// ── 17. Task Templates (Phase 6 — recurring task definitions) ───────────────
// Reusable definitions that the Monday-morning materializer expands into
// concrete Task rows. The relationship is template → many tasks (one per week
// the template was active).
export const TaskTemplateSchema = z.object({
  id: idField,
  title: str,
  description: strOpt,
  assignee: strOpt,
  assigneeRole: strOpt,
  priority: strOpt,
  // Day-of-week the task is due. "Mon"|"Tue"|...|"Sat"; default Mon.
  dueDayOfWeek: strOpt,
  // ISO date the template starts being active. Empty = active from creation.
  startDate: strOpt,
  // ISO date the template stops. Empty = no end.
  endDate: strOpt,
  active: bool.optional(),
  createdBy: strOpt,
  createdAt: strOpt,
}).passthrough();

// ── 18. Leads (Phase A — sales pipeline) ────────────────────────────────────
// The CRM front half. A Lead is a worked sales opportunity: it enters the
// pipeline (usually spawned from an inbound Service Request), moves through
// stages, and on Won hands off to the Jobs module for delivery. Distinct from
// Request_ (raw inbound intake) and Job (client-facing appointment).
//
// stageHistory + value are recorded from day one so Phase F AI scoring has a
// training signal (which lead shapes convert, how long each stage takes).
export const LeadSchema = z.object({
  id: idField,
  contactName: str,
  contactPhone: strOpt,
  contactEmail: strOpt,
  source: strOpt,           // WhatsApp / Phone / Referral / Website / Walk-in / Email
  svc: strOpt,              // requested service (Cleaning / Pest Control / Both)
  loc: strOpt,              // site / address
  stage: strOpt,            // New / Contacted / Quoted / Won / Lost  (default New)
  value: numOpt,            // estimated deal value (₦)
  ownerName: strOpt,        // supervisor who owns follow-up
  nextAction: strOpt,       // free-text next step
  nextActionDate: strOpt,   // YYYY-MM-DD — drives the follow-up nudge
  notes: strOpt,
  clientId: strOpt,         // link to an existing client, if converted/known
  requestId: strOpt,        // origin Service Request id (provenance, dedup)
  jobId: strOpt,            // set when Won → a Job is created
  lostReason: strOpt,       // captured on Lost — feeds future win/loss analysis
  // Append-only stage transitions: [{ stage, at, by }]. z.array(z.any()) to
  // stay permissive; the UI writes a known shape.
  stageHistory: z.array(z.any()).catch([]).optional(),
  createdAt: strOpt,
  updatedAt: strOpt,
  wonAt: strOpt,
}).passthrough();

// ── 19. Workflows (Phase C — automation engine) ─────────────────────────────
// A Workflow is a trigger→action rule: "when X happens, do Y". The engine
// (lib/workflow-engine.ts) evaluates active workflows against app data on
// load — the same idempotent-materializer pattern as the recurring-task
// scheduler. Low volume, so the jsonb dw_* convention is fine here.
export const WorkflowSchema = z.object({
  id: idField,
  name: str,
  active: bool.optional(),
  // lead_created | lead_stage_changed | job_completed | contract_expiring | schedule_due
  trigger: strOpt,
  // Trigger tuning: { stage } for lead_stage_changed; { daysBefore } for
  // contract_expiring / schedule_due. z.record keeps it permissive.
  triggerConfig: z.record(z.any()).catch({}).optional(),
  // create_task | send_whatsapp
  action: strOpt,
  // Action tuning: { title, assignee, assigneeRole } for create_task;
  // { message } for send_whatsapp. Templates may use {placeholders}.
  actionConfig: z.record(z.any()).catch({}).optional(),
  createdBy: strOpt,
  createdAt: strOpt,
}).passthrough();

// ── 20. Workflow runs (Phase C — audit + dedup ledger) ──────────────────────
// One row per firing. dedupKey is the idempotency anchor: the engine never
// fires the same workflow twice for the same subject/context.
export const WorkflowRunSchema = z.object({
  id: idField,
  workflowId: strOpt,
  workflowName: strOpt,
  dedupKey: str,
  firedAt: strOpt,
  subjectType: strOpt,     // lead | job | client | schedule
  subjectId: strOpt,
  subjectLabel: strOpt,    // human-readable ("Acme Office")
  result: strOpt,          // task_created | whatsapp_sent | skipped_no_phone | error
}).passthrough();

// ── 21. Appointments (Phase D — booking + calendar) ─────────────────────────
// A booked visit slot. Distinct from a Job: an Appointment is the calendar
// commitment (who/when/where); confirming it spawns the Job that carries
// delivery (GPS check-in, site report). Public bookings arrive as Requested;
// staff confirm → Confirmed + Job.
export const AppointmentSchema = z.object({
  id: idField,
  contactName: str,
  contactPhone: strOpt,
  svc: strOpt,
  loc: strOpt,
  date: strOpt,             // YYYY-MM-DD
  time: strOpt,             // HH:mm slot start
  durationMins: numOpt,     // default 120
  status: strOpt,           // Requested / Confirmed / Completed / Cancelled
  assignedTo: strOpt,       // supervisor
  jobId: strOpt,            // set on confirm
  leadId: strOpt,           // provenance if booked from pipeline
  source: strOpt,           // public / internal / whatsapp
  notes: strOpt,
  createdAt: strOpt,
}).passthrough();

// ── 22. Invoices (Phase D — payments) ───────────────────────────────────────
// Client-facing bill, usually raised from a completed Job. Paystack refs
// recorded on payment; the edge functions are env-gated so invoices work
// as documents before any payment gateway exists.
export const InvoiceSchema = z.object({
  id: idField,
  invoiceNo: strOpt,        // human number DW-2026-0001
  clientName: str,
  contactPhone: strOpt,
  contactEmail: strOpt,
  jobId: strOpt,
  items: z.array(z.any()).catch([]).optional(),  // { desc, qty, rate }
  subtotal: numOpt,
  vat: numOpt,
  total: numOpt,
  status: strOpt,           // Draft / Sent / Paid / Overdue / Cancelled
  dueDate: strOpt,
  paidAt: strOpt,
  paystackRef: strOpt,
  notes: strOpt,
  createdBy: strOpt,
  createdAt: strOpt,
}).passthrough();

// ── 23. Reviews (Phase E — reputation flywheel) ─────────────────────────────
// A review request is created when a job completes (usually by an automation),
// carrying a token the customer opens at #review/<token>. They rate 1-5 and
// leave a comment; 4-5 gets nudged to Google, 1-3 routes to the team as a
// service-recovery signal instead of a public rating.
export const ReviewSchema = z.object({
  id: idField,
  token: str,               // opaque link key (public page lookup)
  clientName: str,
  contactPhone: strOpt,
  jobId: strOpt,
  svc: strOpt,
  rating: numOpt,           // 0 = not yet answered, else 1..5
  comment: strOpt,
  status: strOpt,           // Requested / Responded / Published / Recovered
  requestedAt: strOpt,
  respondedAt: strOpt,
  requestedBy: strOpt,      // staff name or "automation"
  googlePrompted: bool.optional(),  // did we show the Google CTA (4-5★)
}).passthrough();

// ── 24. Referrals (Phase E — referral engine) ───────────────────────────────
// One code per advocate (usually an existing happy client). Codes are entered
// on the public booking page; conversions link back to the referred lead/job
// so reward payouts are auditable.
export const ReferralSchema = z.object({
  id: idField,
  code: str,                // human-shareable, e.g. ACME-2026
  advocateName: str,        // who refers
  advocatePhone: strOpt,
  rewardNote: strOpt,       // "10% off next service"
  active: bool.optional(),
  uses: numOpt,             // conversions credited
  createdBy: strOpt,
  createdAt: strOpt,
}).passthrough();

// ── Schema registry (table-name → schema) ────────────────────────────────────
// Used by dbLoad/dbSync to look up the right validator. Keys must match the
// `table` arguments passed to those functions (i.e. the part after the `dw_`
// prefix).
export const SCHEMAS = {
  clients: ClientSchema,
  jobs: JobSchema,
  users: UserSchema,
  staff: StaffSchema,
  requests: RequestSchema,
  reports: SiteReportSchema,
  imprests: ImprestSchema,
  inventory: InventorySchema,
  requisitions: RequisitionSchema,
  supplyitems: SupplyItemSchema,
  schedules: ScheduleSchema,
  absences: AbsenceSchema,
  covers: CoverSchema,
  assessments: AssessmentSchema,
  contacts: ContactSchema,
  tasks: TaskSchema,
  tasktemplates: TaskTemplateSchema,
  leads: LeadSchema,
  workflows: WorkflowSchema,
  workflowruns: WorkflowRunSchema,
  appointments: AppointmentSchema,
  invoices: InvoiceSchema,
  reviews: ReviewSchema,
  referrals: ReferralSchema,
} as const;

export type TableName = keyof typeof SCHEMAS;

// ── Typed record aliases derived from each schema ────────────────────────────
// These are the runtime-validated shapes that flow through the React tree.
// Use these in component props and useState() generics instead of `any[]`.
export type Client       = z.infer<typeof ClientSchema>;
export type Job          = z.infer<typeof JobSchema>;
export type AppUser      = z.infer<typeof UserSchema>;
export type Staff        = z.infer<typeof StaffSchema>;
export type Request_     = z.infer<typeof RequestSchema>;  // `Request` clashes with the DOM global
export type SiteReport   = z.infer<typeof SiteReportSchema>;
export type Imprest      = z.infer<typeof ImprestSchema>;
export type Inventory    = z.infer<typeof InventorySchema>;
export type Requisition  = z.infer<typeof RequisitionSchema>;
export type SupplyItem   = z.infer<typeof SupplyItemSchema>;
export type Schedule     = z.infer<typeof ScheduleSchema>;
export type Absence      = z.infer<typeof AbsenceSchema>;
export type Cover        = z.infer<typeof CoverSchema>;
export type Assessment   = z.infer<typeof AssessmentSchema>;
export type Contact      = z.infer<typeof ContactSchema>;
export type Task         = z.infer<typeof TaskSchema>;
export type TaskTemplate = z.infer<typeof TaskTemplateSchema>;
export type Lead         = z.infer<typeof LeadSchema>;
export type Workflow     = z.infer<typeof WorkflowSchema>;
export type WorkflowRun  = z.infer<typeof WorkflowRunSchema>;
export type Appointment  = z.infer<typeof AppointmentSchema>;
export type Invoice      = z.infer<typeof InvoiceSchema>;
export type Review       = z.infer<typeof ReviewSchema>;
export type Referral     = z.infer<typeof ReferralSchema>;

/**
 * Role union. Phase 6 added Finance as a first-class role distinct from
 * Admin — Finance users see imprest/payroll/deduction-focused dashboards
 * but don't have org-wide config powers.
 */
export type UserRole = "Admin" | "Supervisor" | "Finance" | "Technician";

/**
 * Currently logged-in user.
 * After `LoginScreen.onLogin(...)` fires, App.tsx stores this shape.
 * Pages that only read it (Jobs, Settings, SiteReports) destructure
 * `user.role`, `user.name`, `user.email`.
 */
export interface CurrentUser {
  id: string;
  name: string;
  // `string` retained in the union so legacy rows with unrecognized roles
  // don't fail Zod parsing. Code that branches on role should narrow to the
  // four canonical UserRole values.
  role: UserRole | string;
  email?: string;
  username?: string;
  initial?: string;
  pwHash?: string;
  accessToken?: string;
  [key: string]: any; // Forward-compat: extra fields from Supabase Auth response
}

// ── Validation helper used at the Supabase boundary ──────────────────────────
// Returns a CLEANED array — bad rows are coerced to safe defaults, never
// dropped. Logs a single grouped warning if any rows needed correction so we
// can spot data-quality drift in the browser console.
export function validateRows<T extends TableName>(table: T, rows: unknown[]): unknown[] {
  const schema = SCHEMAS[table];
  if (!schema) {
    console.warn(`[Zod] No schema registered for table "${table}" — passing through unchanged`);
    return rows;
  }
  let warnings = 0;
  const cleaned = rows.map((row, idx) => {
    const result = schema.safeParse(row);
    if (result.success) return result.data;
    warnings++;
    // Log just the first issue per row to avoid console flooding
    const issue = result.error.issues[0];
    console.warn(
      `[Zod] ${table}[${idx}] field "${issue?.path.join(".")}" — ${issue?.message}`,
      { rawRow: row }
    );
    // Re-parse with a permissive fallback path: strip the bad fields and
    // let .catch() defaults take over. We still return *something* so the
    // UI never breaks.
    return { ...((row && typeof row === "object") ? row : {}) };
  });
  if (warnings > 0) {
    console.warn(`[Zod] ${table}: ${warnings} row(s) had validation issues (auto-coerced)`);
  }
  return cleaned;
}

// Validate a single record before write. Returns the coerced record, or null
// if the data is unrecoverable (in which case the caller should abort).
export function validateRecord<T extends TableName>(table: T, record: unknown): unknown | null {
  const schema = SCHEMAS[table];
  if (!schema) return record;
  const result = schema.safeParse(record);
  if (result.success) return result.data;
  console.warn(
    `[Zod] Write blocked for ${table} — invalid record:`,
    result.error.issues,
    { rawRecord: record }
  );
  // Even on validation failure, return the original record so writes don't
  // mysteriously disappear. The warning is the signal that something is off.
  // In Phase 4 (strict mode), we'll switch this to `return null`.
  return record;
}
